import { UFS } from '@/lib/localidades';

import { verificadoresConferem } from './documento';
import type { LinhaDeCadastro, TipoDeCadastro } from './tipos';

/**
 * O que cada um dos seis cadastros pergunta, numa tabela so.
 *
 * ## Por que uma tabela, e nao seis formularios
 *
 * Os seis tem a mesma forma: codigo, nome, algumas referencias e uma ou outra
 * coluna propria. Seis componentes de formulario seriam seis lugares para a
 * regra "marque o opcional, nunca o obrigatorio" divergir — e ela divergiria no
 * primeiro campo acrescentado com pressa.
 *
 * ## A marca de OPCIONAL e a unica marca
 *
 * O manual (p.16) e explicito: marca-se o que e opcional, nunca o que e
 * obrigatorio. Quase tudo neste sistema e obrigatorio; tela cheia de asterisco
 * deixa de comunicar qualquer coisa, e a excecao e que precisa ficar visivel.
 * Por isso `opcional` e um campo desta tabela, e o formulario nao tem nem como
 * desenhar um asterisco.
 *
 * ## A validacao daqui NAO e a regra
 *
 * Quem decide e o banco: `check`, indice unico e chave composta. O que estas
 * funcoes fazem e responder ANTES, ao sair do campo, o que o servidor responderia
 * de qualquer jeito — e com a mesma frase. Nenhuma delas substitui a recusa do
 * banco; se as duas discordarem, a do banco vence, porque e ela que grava.
 */

/* `cep` e `municipio` sao especies proprias porque desenham mais do que um
   campo de texto: o CEP leva o botao de busca, e o municipio le a UF
   escolhida para saber que lista oferecer. */
export type EspecieDeCampo = 'texto' | 'selecao' | 'data' | 'cep' | 'municipio' | 'sistema';
/* `sistema` (0131, decisao 57): o campo que o SISTEMA escreve e ninguem
   digita. Aparece so para ler, e nunca vai no corpo da API. */

/** De qual cadastro sai a lista de escolha de uma referencia. */
export type Referencia = 'filiais' | 'clientes' | 'centros-de-custo';

export interface OpcaoFixa {
  readonly valor: string;
  readonly rotulo: string;
}

/**
 * A secao do formulario em que o campo aparece (R01, p.28: campos agrupados em
 * secoes com titulo). A ordem das secoes e a ordem em que os campos aparecem.
 */
export type SecaoDoFormulario = 'Identificação' | 'Vínculos' | 'Localização' | 'Vigência';

export interface CampoDoFormulario {
  /** O nome do campo NO CORPO da API. */
  readonly nome: string;
  readonly rotulo: string;
  readonly especie: EspecieDeCampo;
  readonly secao: SecaoDoFormulario;
  readonly opcional?: boolean;
  /** Frase completa, com ponto final. */
  readonly ajuda?: string;
  /** Para `selecao`: de onde vem a lista. */
  readonly referencia?: Referencia;
  /** Para `selecao` com lista fixa, como o tipo da obra. */
  readonly opcoes?: readonly OpcaoFixa[];
  /** Roda AO SAIR do campo. Devolve a mensagem, ou `null` quando esta bom. */
  readonly validar?: (valor: string) => string | null;
}

const soDigitos = (texto: string) => texto.replace(/\D/g, '');

function validarDocumento(valor: string): string | null {
  const digitos = soDigitos(valor);
  if (digitos === '') return null;
  if (digitos.length !== 11 && digitos.length !== 14) {
    return 'Informe o CPF com 11 dígitos ou o CNPJ com 14. Pode digitar com ponto e traço.';
  }
  /* O DÍGITO TROCADO (31/10/2026): o servidor aceita, e o mesmo fornecedor
     entraria duas vezes com um número que não existe. Avisa, e não impede. */
  return verificadoresConferem(digitos) === false
    ? `Confira o ${digitos.length === 11 ? 'CPF' : 'CNPJ'}: os dois últimos dígitos não batem com os outros. Se estiver igual ao papel, pode gravar.`
    : null;
}

/* `validarUf` saiu: a UF virou lista fechada, e nao ha o que validar num campo
   em que so as 27 opcoes existem. */

function validarCep(valor: string): string | null {
  const digitos = soDigitos(valor);
  if (digitos === '') return null;
  return digitos.length === 8 ? null : 'O CEP tem oito números. Pode digitar com o traço.';
}

const CODIGO: CampoDoFormulario = {
  nome: 'codigo',
  rotulo: 'Código',
  especie: 'texto',
  secao: 'Identificação',
  ajuda: 'O código não se repete dentro da empresa. Ele aparece nas listas e nos relatórios.',
};

/**
 * 0131 · O CODIGO QUE O SISTEMA CRIA (decisao 57, 29/10/2026): filial,
 * cliente, fornecedor, obra e contrato nascem com PREFIXO-NNN (CLI-007), na
 * sequencia da empresa, e ninguem digita nem muda. O centro de custo continua
 * digitado: ele segue o plano de contas da contabilidade.
 */
const CODIGO_DO_SISTEMA: CampoDoFormulario = {
  nome: 'codigo',
  rotulo: 'Código',
  especie: 'sistema',
  secao: 'Identificação',
};

/**
 * O ENDEREÇO, e por que ele deixou de ser texto livre.
 *
 * `uf` e `municipio` eram campos abertos. Isso produz "MG", "Mg", "mg" e
 * "Minas" na mesma coluna, e "Ipatinga", "IPATINGA" e "Ipatinga " na coluna ao
 * lado — quatro grafias e uma cidade. Nenhum relatório por região fecha depois
 * disso, e o conserto retroativo é adivinhação.
 *
 * Agora a UF é lista fechada (as 27 são fato e não mudam desde 1988) e o
 * município sai da lista do IBGE, filtrada pela UF — as opções vêm do
 * formulário, que sabe qual UF está escolhida.
 */
const CEP: CampoDoFormulario = {
  nome: 'cep',
  rotulo: 'CEP',
  especie: 'cep',
  secao: 'Localização',
  opcional: true,
  ajuda: 'Com o CEP, o endereço vem preenchido. Confira antes de gravar.',
  validar: validarCep,
};

const LOGRADOURO: CampoDoFormulario = {
  nome: 'logradouro',
  rotulo: 'Logradouro',
  especie: 'texto',
  secao: 'Localização',
  opcional: true,
};

const NUMERO: CampoDoFormulario = {
  nome: 'numero',
  rotulo: 'Número',
  especie: 'texto',
  secao: 'Localização',
  opcional: true,
};

const BAIRRO: CampoDoFormulario = {
  nome: 'bairro',
  rotulo: 'Bairro',
  especie: 'texto',
  secao: 'Localização',
  opcional: true,
};

/* A UF vem ANTES do município na ordem da seção, e isso não é estética: sem
   ela escolhida, a lista de municípios não tem como existir. */
const UF: CampoDoFormulario = {
  nome: 'uf',
  rotulo: 'UF',
  especie: 'selecao',
  secao: 'Localização',
  opcional: true,
  opcoes: UFS.map((u) => ({ valor: u.sigla, rotulo: `${u.sigla} · ${u.nome}` })),
};

const MUNICIPIO: CampoDoFormulario = {
  nome: 'municipio',
  rotulo: 'Município',
  /* As opções vêm do formulário, que lê a UF escolhida e carrega a lista do
     IBGE sob demanda — são 5.571, e ninguém escolhe numa lista dessas. */
  especie: 'municipio',
  secao: 'Localização',
  opcional: true,
};

const DOCUMENTO: CampoDoFormulario = {
  nome: 'documento',
  rotulo: 'CPF ou CNPJ',
  especie: 'texto',
  secao: 'Identificação',
  opcional: true,
  ajuda: 'Guardado só com os dígitos. É o que impede o mesmo cadastro entrar duas vezes.',
  validar: validarDocumento,
};

export const CAMPOS_DO_CADASTRO: Record<TipoDeCadastro, readonly CampoDoFormulario[]> = {
  filiais: [CODIGO_DO_SISTEMA, { nome: 'nome', rotulo: 'Nome da filial', especie: 'texto', secao: 'Identificação' }, CEP, LOGRADOURO, NUMERO, BAIRRO, UF, MUNICIPIO],

  clientes: [CODIGO_DO_SISTEMA, { nome: 'nome', rotulo: 'Nome do cliente', especie: 'texto', secao: 'Identificação' }, DOCUMENTO],

  fornecedores: [
    CODIGO_DO_SISTEMA,
    { nome: 'nome', rotulo: 'Nome do fornecedor', especie: 'texto', secao: 'Identificação' },
    DOCUMENTO,
  ],

  'centros-de-custo': [
    CODIGO,
    { nome: 'nome', rotulo: 'Nome do centro de custo', especie: 'texto', secao: 'Identificação' },
    { nome: 'filialId', rotulo: 'Filial', especie: 'selecao', secao: 'Vínculos', referencia: 'filiais', opcional: true },
  ],

  obras: [
    CODIGO_DO_SISTEMA,
    { nome: 'nome', rotulo: 'Nome da obra', especie: 'texto', secao: 'Identificação' },
    {
      nome: 'tipo',
      rotulo: 'Tipo',
      especie: 'selecao',
      secao: 'Identificação',
      opcoes: [
        { valor: 'obra', rotulo: 'Obra de cliente' },
        { valor: 'patio', rotulo: 'Pátio próprio' },
        { valor: 'oficina', rotulo: 'Oficina' },
      ],
      ajuda: 'Obra de cliente exige cliente. Pátio e oficina são da própria empresa.',
    },
    {
      nome: 'clienteId',
      rotulo: 'Cliente',
      especie: 'selecao',
      secao: 'Vínculos',
      referencia: 'clientes',
      opcional: true,
      ajuda: 'Obrigatório quando o tipo é obra de cliente.',
    },
    {
      nome: 'centroCustoId',
      rotulo: 'Centro de custo padrão',
      especie: 'selecao',
      secao: 'Vínculos',
      referencia: 'centros-de-custo',
      opcional: true,
      ajuda: 'A alocação pode apontar outro: uma obra troca de centro de custo no meio do contrato.',
    },
    { nome: 'filialId', rotulo: 'Filial', especie: 'selecao', secao: 'Vínculos', referencia: 'filiais', opcional: true },
    CEP,
    LOGRADOURO,
    NUMERO,
    BAIRRO,
    UF,
    MUNICIPIO,
  ],

  contratos: [
    CODIGO_DO_SISTEMA,
    { nome: 'descricao', rotulo: 'Descrição', especie: 'texto', secao: 'Identificação' },
    { nome: 'clienteId', rotulo: 'Cliente', especie: 'selecao', secao: 'Vínculos', referencia: 'clientes' },
    {
      nome: 'centroCustoId',
      rotulo: 'Centro de custo',
      especie: 'selecao',
      secao: 'Vínculos',
      referencia: 'centros-de-custo',
      opcional: true,
    },
    { nome: 'inicio', rotulo: 'Início da vigência', especie: 'data', secao: 'Vigência' },
    {
      nome: 'fim',
      rotulo: 'Fim da vigência',
      especie: 'data',
      secao: 'Vigência',
      opcional: true,
      ajuda: 'Em branco quer dizer vigência aberta, sem data de término.',
    },
  ],
};

/** O valor que cada campo mostra ao abrir o formulario de edicao. */
export function valoresDe(tipo: TipoDeCadastro, linha: LinhaDeCadastro | null): Record<string, string> {
  const valores: Record<string, string> = {};
  for (const campo of CAMPOS_DO_CADASTRO[tipo]) {
    if (linha === null) {
      valores[campo.nome] = campo.nome === 'tipo' ? 'obra' : '';
      continue;
    }
    valores[campo.nome] = leitura(campo, linha);
  }
  return valores;
}

/**
 * A API devolve `cliente_id` e o formulario pergunta `clienteId`; a data volta
 * em ISO 8601 e o campo de data quer `AAAA-MM-DD`. A traducao mora aqui, e nao
 * na tela, para o formulario de edicao nao ter um `if` por cadastro.
 */
function leitura(campo: CampoDoFormulario, linha: LinhaDeCadastro): string {
  const emBanco: Record<string, string> = {
    clienteId: 'cliente_id',
    centroCustoId: 'centro_custo_id',
    filialId: 'filial_id',
  };
  const chave = emBanco[campo.nome] ?? campo.nome;
  const bruto = (linha as unknown as Record<string, unknown>)[chave];
  if (bruto === null || bruto === undefined) return '';
  if (campo.especie === 'data') {
    const quando = new Date(String(bruto));
    if (Number.isNaN(quando.getTime())) return '';
    /* `AAAA-MM-DD` em hora LOCAL. `toISOString()` devolveria UTC, e no fuso de
       Brasília um contrato que começa dia 1 voltaria como dia 31 do mês
       anterior — um dia de erro, silencioso, num campo que já vem preenchido. */
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${quando.getFullYear()}-${dois(quando.getMonth() + 1)}-${dois(quando.getDate())}`;
  }
  return String(bruto);
}

/** O corpo que vai para a API, a partir do que esta escrito no formulario. */
export function corpoDe(
  tipo: TipoDeCadastro,
  valores: Record<string, string>,
  motivo: string,
): Record<string, unknown> {
  const corpo: Record<string, unknown> = {};

  for (const campo of CAMPOS_DO_CADASTRO[tipo]) {
    /* O campo do sistema (o codigo, 0131) nunca vai: a API o recusa. */
    if (campo.especie === 'sistema') continue;
    const bruto = (valores[campo.nome] ?? '').trim();

    if (campo.especie === 'selecao' && campo.referencia) {
      corpo[campo.nome] = bruto === '' ? null : bruto;
      continue;
    }
    if (campo.especie === 'data') {
      /* Meia-noite LOCAL, e nao `new Date('2026-01-01')`, que o JavaScript lê
         como meia-noite UTC e no Brasil cai no dia anterior. */
      corpo[campo.nome] = bruto === '' ? null : new Date(`${bruto}T00:00:00`).toISOString();
      continue;
    }
    if (campo.nome === 'documento') {
      corpo[campo.nome] = soDigitos(bruto);
      continue;
    }
    corpo[campo.nome] = bruto;
  }

  const limpo = motivo.trim();
  if (limpo) corpo['motivo'] = limpo;
  return corpo;
}

/**
 * O que falta preencher, na ordem em que os campos aparecem.
 *
 * Roda no ENVIO, nao a cada tecla: a validacao por campo acontece ao sair dele
 * (p.16), e esta aqui e a rede que impede um envio que o servidor recusaria.
 */
export function faltando(tipo: TipoDeCadastro, valores: Record<string, string>): readonly string[] {
  return CAMPOS_DO_CADASTRO[tipo]
    .filter((campo) => campo.especie !== 'sistema' && !campo.opcional && (valores[campo.nome] ?? '').trim() === '')
    .map((campo) => campo.rotulo);
}

/**
 * Onde o formulario daquele cadastro abre — e a R01 (p.29) quem decide, nao a
 * tela: "o tipo define o lugar; nenhuma janela e posicionada a mao".
 *
 * A regra da p.29 tem tres faixas, e a unica com NUMERO e esta: "pagina
 * inteira: cadastro completo, mais de 10 campos ou varias secoes". Por isso a
 * conta e pelo numero de campos, e nao por "varias secoes" — quantas sao
 * "varias" o manual nao diz, e regra que nao da pra contar nao se cobra.
 *
 * Ela e DERIVADA da tabela de campos, de proposito. Uma lista a mao dizendo
 * quais cadastros abrem em pagina envelheceria no primeiro campo novo: obras
 * ja passou de 9 para 12 campos desde a revisao de 30/09, e a tela continuou
 * abrindo o formulario numa janela de 640 px, que empilha os 12 numa coluna so
 * com rolagem interna — exatamente o aperto que a faixa da pagina existe para
 * evitar.
 */
export function lugarDoFormulario(tipo: TipoDeCadastro): 'janela' | 'pagina' {
  return CAMPOS_DO_CADASTRO[tipo].length > 10 ? 'pagina' : 'janela';
}
